import { useCallback } from 'react';
import { useFormContext } from '../components/FormProvider';
import { useFormValidation } from './useFormValidation';

export function useFormData() {
  const { 
    formData, 
    updateField, 
    updateStepData, 
    getFieldError,
    clearValidationError 
  } = useFormContext();
  
  const { validateField } = useFormValidation();

  // Handle single field updates with validation
  const handleFieldChange = useCallback((field, value) => {
    // Update the field
    updateField(field, value);
    
    // Validate the field
    validateField(field, value);
  }, [updateField, validateField]);

  // Handle multiple field updates
  const handleStepDataChange = useCallback((data) => {
    updateStepData(data);
    
    // Validate all changed fields
    Object.entries(data).forEach(([field, value]) => {
      validateField(field, value);
    });
  }, [updateStepData, validateField]);

  // Handle form control changes (for MDB components)
  const handleFormControlChange = useCallback((event) => {
    const { name, value, type, checked, files } = event.target;
    
    let fieldValue = value;
    
    // Handle different input types
    switch (type) {
      case 'checkbox':
        fieldValue = checked;
        break;
      case 'file':
        fieldValue = files ? files[0] : null;
        break;
      default:
        fieldValue = value;
    }

    handleFieldChange(name, fieldValue);
  }, [handleFieldChange]);

  // Handle array field updates (for speakers, conditions, etc.)
  const handleArrayFieldChange = useCallback((field, index, value) => {
    const currentArray = formData[field] || [];
    const newArray = [...currentArray];
    newArray[index] = value;
    
    handleFieldChange(field, newArray);
  }, [formData, handleFieldChange]);

  // Add item to array field
  const addArrayItem = useCallback((field, item) => {
    const currentArray = formData[field] || [];
    const newArray = [...currentArray, item];
    
    handleFieldChange(field, newArray);
  }, [formData, handleFieldChange]);

  // Remove item from array field
  const removeArrayItem = useCallback((field, index) => {
    const currentArray = formData[field] || [];
    const newArray = currentArray.filter((_, i) => i !== index);
    
    handleFieldChange(field, newArray);
  }, [formData, handleFieldChange]);

  // Get field value with default
  const getFieldValue = useCallback((field, defaultValue = '') => {
    return formData[field] ?? defaultValue;
  }, [formData]);

  // Check if field has error
  const hasFieldError = useCallback((field) => {
    return !!getFieldError(field);
  }, [getFieldError]);

  // Clear field error
  const clearFieldError = useCallback((field) => {
    clearValidationError(field);
  }, [clearValidationError]);

  // Get formatted field props for MDB components
  const getFieldProps = useCallback((field, options = {}) => {
    const { type = 'text', required = false, ...rest } = options;
    
    return {
      name: field,
      value: getFieldValue(field),
      onChange: handleFormControlChange,
      invalid: hasFieldError(field),
      required,
      type,
      ...rest
    };
  }, [getFieldValue, handleFormControlChange, hasFieldError]);

  return {
    // Data
    formData,
    getFieldValue,
    
    // Field operations
    handleFieldChange,
    handleStepDataChange,
    handleFormControlChange,
    getFieldProps,
    
    // Array operations
    handleArrayFieldChange,
    addArrayItem,
    removeArrayItem,
    
    // Validation
    hasFieldError,
    getFieldError,
    clearFieldError
  };
}